import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogOpenEvent } from '@theme/dialog';
import { rankLook } from '@theme/latea-look-ranking';

const visitSeed = crypto.getRandomValues(new Uint32Array(1))[0];
const recommendationCache = new Map();
const unavailableIds = new Set();
let adding = false;
let lastAdded = null;
let sharedCatalog;

function catalog() {
  if (sharedCatalog) return sharedCatalog;
  try {
    sharedCatalog = JSON.parse(document.getElementById('LateaLookCatalog')?.textContent ?? '{}');
  } catch {
    sharedCatalog = { products: [], best: [] };
  }
  return sharedCatalog;
}

function recentlyViewed() {
  try {
    const ids = RecentlyViewed.getProducts();
    return Array.isArray(ids) ? ids.map(String) : [];
  } catch {
    return [];
  }
}

function makeElement(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function money(amount) {
  const data = catalog();
  try {
    return new Intl.NumberFormat(data.locale ?? document.documentElement.lang, { style: 'currency', currency: data.currency ?? 'EUR' }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} €`;
  }
}

class LateaLookComponent extends Component {
  requiredRefs = ['config', 'rail', 'feedback', 'heading'];
  #lifecycle;
  #network;
  #observer;
  #markupObserver;
  #refreshFrame;
  #configSignature = '';
  #timer;
  #frame;
  #cart = { count: 0, ids: [], items: [] };
  #labels = {};
  #signature = '';
  #listSignature = '';
  #complementary = [];
  #related = [];

  connectedCallback() {
    super.connectedCallback();
    this.#lifecycle?.abort();
    this.#lifecycle = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal: this.#lifecycle.signal });
    const drawer = this.closest('cart-drawer-component');
    if (drawer) drawer.addEventListener(DialogOpenEvent.eventName, this.#onOpen, { signal: this.#lifecycle.signal });
    this.#read();
    this.#render();
    this.#markupObserver?.disconnect();
    this.#markupObserver = new MutationObserver(() => {
      const config = this.querySelector('[ref="config"]')?.textContent;
      const rail = this.querySelector('[ref="rail"]');
      if (config !== this.#configSignature || (!this.hidden && this.#cart.count && rail && !rail.children.length)) {
        cancelAnimationFrame(this.#refreshFrame);
        this.#refreshFrame = requestAnimationFrame(() => {
          if (this.isConnected) this.updatedCallback();
        });
      }
    });
    this.#markupObserver.observe(this, { childList: true, subtree: true, characterData: true });
    if (drawer) {
      if (drawer.querySelector('dialog[open]')) this.#schedule();
    } else {
      this.#observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#observer?.disconnect();
          this.#schedule();
        }
      }, { rootMargin: '100px' });
      this.#observer.observe(this);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listSignature = '';
    this.#read();
    this.#render();
    if (this.#visible()) this.#schedule();
  }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#network?.abort();
    this.#observer?.disconnect();
    this.#markupObserver?.disconnect();
    cancelAnimationFrame(this.#refreshFrame);
    clearTimeout(this.#timer);
    cancelAnimationFrame(this.#frame);
    super.disconnectedCallback();
  }

  #visible() {
    const drawer = this.closest('cart-drawer-component');
    return !this.hidden && (!drawer || Boolean(drawer.querySelector('dialog[open]')));
  }

  #read() {
    try {
      const data = JSON.parse(this.refs.config.textContent);
      this.#configSignature = this.refs.config.textContent;
      this.#cart = { count: data.count, ids: data.ids, items: data.items };
      this.#labels = data.labels;
    } catch {
      return;
    }
    this.hidden = this.#cart.count === 0;
  }

  #onOpen = () => {
    this.updatedCallback();
    this.#schedule();
  };

  #onCartUpdate = (event) => {
    const resource = event.detail?.resource;
    if (Array.isArray(resource?.items)) {
      const products = new Map((catalog().products ?? []).map((product) => [String(product.id), product]));
      const jewels = resource.items.filter((item) => item.product_type !== 'Packaging');
      this.#cart = {
        count: jewels.reduce((quantity, item) => quantity + item.quantity, 0),
        ids: resource.items.map((item) => item.product_id),
        items: resource.items.map((item) => ({ ...products.get(String(item.product_id)), id: item.product_id, type: item.product_type, title: item.product_title, variantTitle: item.variant_title, quantity: item.quantity })),
      };
      this.hidden = this.#cart.count === 0;
      this.#render();
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        if (this.isConnected) this.updatedCallback();
      });
      if (this.#visible()) this.#schedule();
    }
  };

  #schedule() {
    clearTimeout(this.#timer);
    if (!this.#cart.count || !this.#visible()) return;
    this.#timer = setTimeout(() => this.#enrich(), 60);
  }

  async #enrich() {
    const signature = JSON.stringify([this.#cart.ids, this.#cart.count]);
    if (signature === this.#signature) return;
    this.#signature = signature;
    this.#network?.abort();
    this.#network = new AbortController();
    const controller = this.#network;
    const timeout = setTimeout(() => controller.abort(), 2500);
    const anchors = [...new Set(this.#cart.items.filter((item) => item.type !== 'Packaging').map((item) => item.id))].slice(0, 2);
    const base = window.Shopify?.routes?.root ?? '/';
    const jobs = anchors.flatMap((id) => ['complementary', 'related'].map(async (intent) => {
      const key = `${id}:${intent}`;
      const cached = recommendationCache.get(key);
      if (cached && Date.now() - cached.time < 300000) return { intent, ids: cached.ids };
      const url = new URL(`${base}recommendations/products.json`, window.location.origin);
      url.searchParams.set('product_id', id);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', intent);
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const result = await response.json();
      const ids = (result.products ?? []).filter((product) => product.available).map((product) => product.id);
      recommendationCache.set(key, { ids, time: Date.now() });
      return { intent, ids };
    }));
    const results = await Promise.allSettled(jobs);
    clearTimeout(timeout);
    if (!this.isConnected || this.#network !== controller) return;
    this.#complementary = results.filter((result) => result.status === 'fulfilled' && result.value.intent === 'complementary').flatMap((result) => result.value.ids);
    this.#related = results.filter((result) => result.status === 'fulfilled' && result.value.intent === 'related').flatMap((result) => result.value.ids);
    this.#render();
  }

  #render() {
    if (!this.#cart.count || !this.#labels.label) {
      this.hidden = true;
      return;
    }
    const data = catalog();
    const products = rankLook({ products: data.products ?? [], cart: this.#cart, recent: recentlyViewed(), complementary: this.#complementary, related: this.#related, best: data.best ?? [], seed: visitSeed, unavailable: [...unavailableIds] });
    this.hidden = this.#cart.count === 0 || !products.length;
    const signature = products.map((product) => product.id).join(',');
    if (signature !== this.#listSignature || this.refs.rail.children.length !== products.length) {
      const scroll = this.refs.rail.scrollLeft;
      const focusedId = this.refs.rail.contains(document.activeElement) ? document.activeElement.closest('[data-product-id]')?.dataset.productId : null;
      this.refs.rail.replaceChildren(...products.map((product) => this.#card(product)));
      this.refs.rail.scrollLeft = scroll;
      this.#listSignature = signature;
      if (focusedId) {
        const sameCard = [...this.refs.rail.children].find((card) => card.dataset.productId === focusedId);
        (sameCard?.querySelector('button') ?? this.refs.rail.querySelector('button') ?? this.refs.heading).focus({ preventScroll: true });
      }
    }
    if (lastAdded && Date.now() < lastAdded.expires) {
      this.refs.feedback.textContent = this.#labels.added.replace('__PRODUCT__', lastAdded.title);
    }
    for (const button of this.querySelectorAll('.latea-look__add')) button.disabled = adding;
  }

  #card(product) {
    const card = makeElement('article', 'latea-look__card');
    card.setAttribute('role', 'listitem');
    card.dataset.productId = String(product.id);
    const media = makeElement('a', 'latea-look__image');
    media.href = product.url;
    if (product.image) {
      const image = makeElement('img');
      image.src = product.image;
      image.alt = product.title;
      image.width = 160;
      image.height = 160;
      image.loading = 'lazy';
      media.append(image);
    }
    const title = makeElement('a', 'latea-look__name', product.title);
    title.href = product.url;
    card.append(media, title);
    if (product.variantTitle !== 'Default Title') card.append(makeElement('span', 'latea-look__variant', product.variantTitle));
    const bottom = makeElement('div', 'latea-look__bottom');
    const form = makeElement('form');
    form.action = Theme.routes.cart_add_url;
    form.method = 'post';
    form.setAttribute('on:submit', '/add');
    form.dataset.productId = String(product.id);
    form.dataset.title = product.title;
    const input = makeElement('input');
    input.type = 'hidden';
    input.name = 'id';
    input.value = String(product.variant);
    const quantity = makeElement('input');
    quantity.type = 'hidden';
    quantity.name = 'quantity';
    quantity.value = '1';
    const button = makeElement('button', 'latea-look__add');
    button.type = 'submit';
    button.setAttribute('aria-label', this.#labels.label.replace('__PRODUCT__', `${product.title}${product.variantTitle === 'Default Title' ? '' : `, ${product.variantTitle}`}`));
    button.append(makeElement('span', 'latea-look__desktop', this.#labels.add));
    const compact = makeElement('span', 'latea-look__mobile', '+');
    compact.setAttribute('aria-hidden', 'true');
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    for (const [key, value] of Object.entries({ width: '17', height: '17', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.5' })) svg.setAttribute(key, value);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M6 7h12l1 14H5L6 7ZM9 8V6a3 3 0 0 1 6 0v2');
    svg.append(path);
    compact.append(svg);
    button.append(compact);
    form.append(input, quantity, button);
    bottom.append(makeElement('span', 'latea-look__price', money(product.price)), form);
    card.append(bottom);
    return card;
  }

  previous() { this.#scroll(-1); }
  next() { this.#scroll(1); }
  #scroll(direction) {
    this.refs.rail.scrollBy({ left: direction * this.refs.rail.clientWidth * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  async add(event) {
    event.preventDefault();
    if (adding || !(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    const variantId = Number(new FormData(form).get('id'));
    const productId = form.dataset.productId;
    if (!variantId || this.#cart.ids.map(String).includes(productId)) return;
    adding = true;
    for (const button of document.querySelectorAll('.latea-look__add')) button.disabled = true;
    this.refs.feedback.textContent = this.#labels.busy;
    const source = `latea-look-${productId}`;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((component) => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
    let result;
    try {
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname }) });
      result = await response.json();
      if (!response.ok || result.status) {
        if (response.status === 422) unavailableIds.add(productId);
        throw new Error(result.description ?? result.message ?? this.#labels.error);
      }
    } catch (error) {
      if (this.isConnected) {
        this.refs.feedback.textContent = unavailableIds.has(productId) ? this.#labels.unavailable : this.#labels.error;
        this.#render();
      }
      adding = false;
      for (const button of document.querySelectorAll('.latea-look__add')) button.disabled = false;
      return;
    }
    lastAdded = { title: form.dataset.title, expires: Date.now() + 3500 };
    let cart;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, { headers: { Accept: 'application/json' } });
      if (response.ok) cart = await response.json();
    } catch {}
    if (!cart) {
      const sectionHtml = Object.values(result.sections ?? {}).find((html) => typeof html === 'string' && html.includes('cartItemCount'));
      const count = sectionHtml ? Number(new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent) : this.#cart.count + 1;
      cart = { item_count: count || this.#cart.count + 1 };
      this.#cart.ids.push(productId);
      this.#cart.count = cart.item_count;
    }
    adding = false;
    const cartEvent = new CartAddEvent(cart, source, { source: 'latea-look', productId, variantId: String(variantId), itemCount: cart.item_count, sections: result.sections });
    document.dispatchEvent(cartEvent);
    for (const button of document.querySelectorAll('.latea-look__add')) button.disabled = false;
    this.#frame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#render();
      this.refs.feedback.textContent = this.#labels.added.replace('__PRODUCT__', lastAdded.title);
      if (!document.activeElement || document.activeElement === document.body) (this.refs.rail.querySelector('button') ?? this.refs.heading).focus({ preventScroll: true });
    });
  }
}

if (!customElements.get('latea-look-component')) customElements.define('latea-look-component', LateaLookComponent);


